import React from 'react';
import { API } from '../api';
import { Toggle } from './ui';
import PosterLightbox from './PosterLightbox';
import PosterPlacer from './PosterPlacer';

/*
 * The posters a club has made, and what can still be done to them.
 *
 * A poster carries at most one step of undo. That shapes this screen more
 * than anything else: after a refinement there are two versions in play and
 * the admin has a decision to make — keep it or take it back — so the card
 * says so plainly instead of leaving a silent backup lying around.
 *
 * Thumbnails come from the server as a stored 480px copy. Drawing this
 * gallery from the full-size images meant downloading several megabytes to
 * show a few small pictures.
 */

const fmtDate = iso => {
  if (!iso) return '';
  const d = new Date(String(iso).slice(0, 10) + 'T00:00:00');
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' });
};

function PosterCard({ poster, onChanged, onBusy, onEnlarge, onMoveLogo, showToast, remaining }) {
  const [instruction, setInstruction] = React.useState('');
  const [refining, setRefining] = React.useState(false);
  const [working, setWorking] = React.useState('');
  const [error, setError] = React.useState('');

  const call = async (path, body, label) => {
    setError('');
    setWorking(label);
    onBusy(true);
    try {
      const res = await fetch(`${API}/api/posters/${poster.id}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.error || 'That did not work.'); return false; }
      onChanged(data);
      return true;
    } catch (e) {
      setError(e.message || 'That did not work.');
      return false;
    } finally {
      setWorking('');
      onBusy(false);
    }
  };

  const refine = async () => {
    if (!instruction.trim()) return;
    const ok = await call('/refine', { instruction: instruction.trim() }, 'Refining');
    if (ok) { setInstruction(''); setRefining(false); showToast('Refined — keep it or undo it.'); }
  };

  const patch = async body => {
    /* Switching a poster on teaches the club's house style, which means a
       call to a language model and a couple of seconds. Say so rather than
       leaving a switch that appears not to have moved. */
    const learns = body.active === true;
    if (learns) setWorking('Learning');
    try {
      const res = await fetch(`${API}/api/posters/${poster.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (!res.ok) return;
      const { styleHistory, ...updated } = await res.json();
      onChanged({ poster: updated, ...(styleHistory !== undefined ? { styleHistory } : {}) });
    } finally {
      if (learns) setWorking('');
    }
  };

  const remove = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${poster.title}"? This cannot be undone.`)) return;
    const res = await fetch(`${API}/api/posters/${poster.id}`, { method: 'DELETE' });
    if (res.ok) { onChanged({ deleted: poster.id }); showToast('Poster deleted.'); }
  };

  const noAllowance = remaining <= 0;

  return (
    <div className="poster-card">
      {/* The picture is the whole point of a poster, and a card this size
          cannot show whether the date on it is right. */}
      {poster.thumbData || poster.imageData
        ? (
          <button type="button" className="poster-thumb-button"
            onClick={() => onEnlarge(poster)}
            aria-label={`Enlarge "${poster.title}"`}>
            <img className="poster-thumb" src={poster.thumbData || poster.imageData} alt="" />
            <span className="poster-thumb-hint" aria-hidden="true">Click to enlarge</span>
          </button>
        )
        : <div className="poster-thumb poster-thumb-empty" aria-hidden="true" />}

      <div className="poster-card-body">
        <input
          className="poster-title-input"
          value={poster.title}
          aria-label="Poster title"
          onChange={e => onChanged({ poster: { ...poster, title: e.target.value } })}
          onBlur={e => patch({ title: e.target.value })} />

        <div className="poster-meta">
          {poster.Competition ? <span className="poster-tag">{poster.Competition.name}</span> : null}
          {poster.Event ? <span className="poster-tag">{poster.Event.name}</span> : null}
          <span>{poster.size}</span>
          {poster.generations > 1 ? <span>{poster.generations} goes</span> : null}
          {poster.showUntil ? <span>until {fmtDate(poster.showUntil)}</span> : null}
        </div>

        {/* A refinement leaves two versions in play. Until the admin decides,
            that decision is the most important thing on the card. */}
        {poster.hasBackup && (
          <div className="poster-decide">
            <div className="poster-decide-text">
              {poster.lastInstruction
                ? <>Changed: &ldquo;{poster.lastInstruction}&rdquo;</>
                : 'This has an earlier version.'}
            </div>
            <div className="poster-actions">
              <button type="button" className="w3-button w3-round poster-btn-primary"
                disabled={!!working} onClick={() => call('/accept', null, 'Keeping')}>
                {working === 'Keeping' ? 'Keeping…' : 'Keep this'}
              </button>
              <button type="button" className="w3-button w3-round poster-btn"
                disabled={!!working} onClick={() => call('/undo', null, 'Undoing')}>
                {working === 'Undoing' ? 'Undoing…' : 'Undo'}
              </button>
            </div>
          </div>
        )}

        {refining ? (
          <div className="poster-refine">
            <textarea
              className="w3-input" aria-label="What to change"
              style={{ minHeight: 64, resize: 'vertical' }}
              placeholder="Make the sky more dramatic. Move the date under the heading."
              value={instruction}
              onChange={e => setInstruction(e.target.value)} />
            <div className="poster-actions">
              <button type="button" className="w3-button w3-round poster-btn-primary"
                disabled={!instruction.trim() || !!working || noAllowance}
                onClick={refine}>
                {working === 'Refining' ? 'Refining…' : 'Generate the change'}
              </button>
              <button type="button" className="w3-button w3-round poster-btn"
                disabled={!!working} onClick={() => { setRefining(false); setInstruction(''); }}>
                Cancel
              </button>
            </div>
            <div className="poster-note">
              {noAllowance
                ? 'No generations or credits left.'
                : 'This is another generation: it uses the month’s allowance first, then a credit. It replaces the current image, and you can undo once.'}
            </div>
          </div>
        ) : (
          <div className="poster-actions">
            {/* Only while the clean copy is still kept — which is until the
                poster goes on the screen. Offering it afterwards would be
                offering something that returns an error. Free, and next to
                a refinement that is not, because a logo in the wrong place
                is the commonest reason to reach for a refinement and the
                worst reason to pay for one. */}
            {poster.canMoveFurniture && (
              <button type="button" className="w3-button w3-round poster-btn"
                disabled={!!working} onClick={onMoveLogo}>
                Move the logo
              </button>
            )}
            <button type="button" className="w3-button w3-round poster-btn"
              disabled={!!working} onClick={() => setRefining(true)}>
              Not right? Refine it
            </button>
            <button type="button" className="w3-button w3-round poster-btn poster-btn-danger"
              disabled={!!working} onClick={remove}>
              Delete
            </button>
          </div>
        )}

        {error && <div className="poster-error" role="alert">{error}</div>}

        <div className="poster-onscreen">
          <Toggle
            ariaLabel={`Show "${poster.title}" on the club screen`}
            checked={!!poster.active}
            onChange={() => patch({ active: !poster.active })} />
          <span>{working === 'Learning' ? 'Learning your style…' : 'On the screen'}</span>
          <label className="poster-until">
            <span>until</span>
            <input type="date" className="w3-input" value={poster.showUntil ? String(poster.showUntil).slice(0, 10) : ''}
              aria-label="Show until"
              onChange={e => patch({ showUntil: e.target.value || null })} />
          </label>
        </div>
      </div>
    </div>
  );
}

function PosterGallery({ posters, onChanged, onBusy, showToast, remaining, openPlacerFor }) {
  /* Held by id rather than by object: a refinement replaces the poster while
     the lightbox is open, and holding the old object would keep showing the
     picture that was just replaced. */
  const [zoomId, setZoomId] = React.useState(null);
  const zoomed = zoomId == null ? null : posters.find(p => p.id === zoomId);
  // Same reasoning: held by id, so saving a move keeps showing the right one.
  const [placeId, setPlaceId] = React.useState(null);
  const placing = placeId == null ? null : posters.find(p => p.id === placeId);

  /* A poster that has just been generated opens straight into the placer,
     because that is the moment the admin is looking at where the logo landed
     and deciding whether it is right. Asking them to find a button afterwards
     means most of them never do, and the whole point is that disagreeing with
     the measurement should be the easy path rather than a discovery. */
  /* Opened at most once per poster. The effect below has to watch `posters`,
     because the poster arrives in the list a beat after its id does — and
     without this it would reopen the placer every time anything in the list
     changed, including closing the placer itself. */
  const autoOpened = React.useRef(new Set());
  React.useEffect(() => {
    if (openPlacerFor == null || autoOpened.current.has(openPlacerFor)) return;
    const fresh = posters.find(p => p.id === openPlacerFor);
    if (!fresh) return;
    autoOpened.current.add(openPlacerFor);
    // Nothing to position on a poster carrying neither a logo nor a code.
    if (fresh.canMoveFurniture) setPlaceId(openPlacerFor);
  }, [openPlacerFor, posters]);

  if (!posters.length) {
    return (
      <p style={{ margin: 0, color: 'var(--gs-text-muted)' }}>
        No posters yet. Make one and it will appear here.
      </p>
    );
  }
  return (
    <>
      <div className="poster-grid">
        {posters.map(p => (
          <PosterCard key={p.id} poster={p} onChanged={onChanged} onBusy={onBusy}
            onEnlarge={() => setZoomId(p.id)}
            onMoveLogo={() => setPlaceId(p.id)}
            showToast={showToast} remaining={remaining} />
        ))}
      </div>
      {zoomed && (
        <PosterLightbox
          poster={zoomed}
          meta={[zoomed.size, (zoomed.Competition && zoomed.Competition.name)
            || (zoomed.Event && zoomed.Event.name)]
            .filter(x => x && x !== zoomed.title).join(' · ')}
          onClose={() => setZoomId(null)} />
      )}
      {placing && (
        <PosterPlacer
          poster={placing}
          onSaved={onChanged}
          showToast={showToast}
          onClose={() => setPlaceId(null)} />
      )}
    </>
  );
}

export default PosterGallery;
